import { Forbidden } from '@risksmart-app/components/src/errors/errors';
import type { FC, ReactNode } from 'react';

import { useCheckNavigationVisibility } from '@/hooks/queries/permission/useCheckNavigationVisibility';
import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';

import { NAVIGATION_VISIBILITY_PARENT_TYPES } from './navigationVisibility';
import type { ObjectAccess } from './Permission';
import { useHasPermissionQuery } from './useHasPermission';

type Props = {
  children: ReactNode;
  permission: ObjectAccess | ObjectAccess[];
  fallback?: ReactNode;
  canHaveAccessAsContributor?: true;
};

const ProtectedRoute: FC<Props> = ({
  children,
  permission,
  fallback,
  canHaveAccessAsContributor,
}) => {
  const { hasPermission: hasAccess, loading } = useHasPermissionQuery(
    permission,
    undefined,
    canHaveAccessAsContributor
  );
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');
  const permissions = Array.isArray(permission) ? permission : [permission];
  const checkInheritedRead =
    trpcEnabled &&
    !hasAccess &&
    NAVIGATION_VISIBILITY_PARENT_TYPES.some((parentType) =>
      permissions.includes(`read:${parentType}`)
    );
  const navigation = useCheckNavigationVisibility(
    NAVIGATION_VISIBILITY_PARENT_TYPES,
    !checkInheritedRead
  );
  // Register entry can follow inherited reads; row access and mutations still
  // use their own permission checks. Never treat visibility as a write grant.
  const hasInheritedRead =
    checkInheritedRead &&
    navigation.data?.some(
      ({ parentType, visible }) =>
        visible && permissions.includes(`read:${parentType}`)
    );

  if (
    hasAccess === null ||
    loading ||
    (checkInheritedRead && navigation.loading)
  ) {
    return <></>;
  }
  if (!hasAccess && !hasInheritedRead) {
    if (fallback) {
      return fallback;
    }
    throw new Forbidden(`Access to ${permission} denied`);
  }

  return children;
};

export default ProtectedRoute;
